import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { copyToClipboard } from "~/common/functions";
import { useAuth } from "~/features/authn/context";
import { POLICIES } from "~/features/authz/authorization";
import { useReferenceActivation, useReferenceDetail } from "~/features/references/hooks";
import { toReferenceDetailUI } from "~/features/references/utils/transformers";
import { ReferenceActivationDialog } from "./ReferenceActivationDialog";
import { ReferenceDetailPresenter } from "./ReferenceDetailPresenter";
import { ReferenceFileAddDialog } from "./ReferenceFileAddDialog";
import { ReferenceFileEditDialog } from "./ReferenceFileEditDialog";
import { ReferenceFormDialog } from "./ReferenceFormDialog";

interface ReferenceDetailProps {
  id: string;
  onBack: () => void;
  variant?: "page" | "peek";
}

export function ReferenceDetail({ id, onBack, variant = "page" }: ReferenceDetailProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data, refetch } = useReferenceDetail(id);
  const { onActivate, onDeactivate, processing } = useReferenceActivation();

  const [editOpen, setEditOpen] = useState(false);
  const [fileAddOpen, setFileAddOpen] = useState(false);
  const [fileEditOpen, setFileEditOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const raw = data.references[0];
  const detail = useMemo(() => (raw ? toReferenceDetailUI(raw) : null), [raw]);
  const canDeactivate = user ? POLICIES["references:deactivate"](user) : false;

  const handleCopyId = useCallback(
    async (value: string) => {
      try {
        await copyToClipboard(value);
        toast.success(t("feature.common.idCopied"));
      } catch {
        toast.error(t("feature.common.copyToClipboardFailed"));
      }
    },
    [t],
  );

  const handleConfirm = useCallback(async () => {
    if (!raw) return;
    const ok = await (raw.deactivatedAt ? onActivate(raw.id) : onDeactivate(raw.id));
    setConfirmOpen(false);
    if (ok) void refetch();
  }, [raw, onActivate, onDeactivate, refetch]);

  const openViewer = useCallback(
    (referenceFileId: string) => {
      window.open(`/reference-files/${id}/${referenceFileId}`, "_blank", "noopener,noreferrer,height=740,width=520");
    },
    [id],
  );

  if (!raw || !detail) {
    return <div className="mx-auto max-w-[var(--content-max)] p-8 text-[13px] text-fg-3">{t("ui.notFound")}</div>;
  }

  return (
    <>
      <ReferenceDetailPresenter
        detail={detail}
        variant={variant}
        canDeactivate={canDeactivate}
        onBack={onBack}
        onCopyId={handleCopyId}
        onEdit={() => setEditOpen(true)}
        onRequestToggleActivation={() => setConfirmOpen(true)}
        onAddFile={() => setFileAddOpen(true)}
        onReplaceFile={() => setFileAddOpen(true)}
        onEditFile={() => setFileEditOpen(true)}
        onViewFile={openViewer}
      />

      <ReferenceFormDialog open={editOpen} onOpenChange={setEditOpen} referenceId={id} />
      <ReferenceFileAddDialog
        open={fileAddOpen}
        onOpenChange={setFileAddOpen}
        referenceId={id}
        replace={detail.file != null}
      />
      {detail.file && (
        <ReferenceFileEditDialog
          open={fileEditOpen}
          onOpenChange={setFileEditOpen}
          referenceId={id}
          referenceFileId={detail.file.id}
          folioOffset={detail.file.folioOffset}
        />
      )}
      <ReferenceActivationDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        deactivate={!detail.isDeactivated}
        processing={processing}
        onConfirm={handleConfirm}
      />
    </>
  );
}
